import { useCallback, useMemo } from "react";
import { keyBy, mapValues } from "lodash-es";

import {
  ActiveToolFacetType,
  IActiveFacet,
  IActiveFacetOption,
  IToolFacet,
  ToolFacetType,
} from "types";
import { i18nService } from "services";

import { mapFacetSlugToKey } from "../";

interface useActiveFacetsProps {
  queryTerm: string | null | undefined;
  facets: IToolFacet[];
  queryFacets: { [key: string]: any };
  setQueryFacets: (activeFacet: ActiveToolFacetType) => void;
  resetTerm: () => void;
}

export const useActiveFacets = ({
  facets,
  queryTerm,
  queryFacets,
  setQueryFacets,
  resetTerm,
}: useActiveFacetsProps) => {
  // default values for facets form
  // eg. { term: "test", theme: ["id1" ,"id2" , "id3"]: type: [] }
  const activeFacetsMap: ActiveToolFacetType = useMemo(
    () => ({
      [ToolFacetType.term]: queryTerm, // hardcode term
      ...mapFacetSlugToKey(facets, queryFacets),
    }),
    [facets, queryTerm, queryFacets]
  );

  // lookup table for facet option labels
  // eg. { theme: { id1: "Cyberpesten" } }
  const facetOptionLabels = useMemo(
    () =>
      mapValues(keyBy(facets, "key"), (value) =>
        mapValues(keyBy(value.options, "id"), (value) => value.label)
      ),
    [facets]
  );

  // get active facet options
  // with an onClick handler to remove the active option
  // eg. [{ label:"Cyberpesten", onClick: () => void }]
  const getActiveOptions = useCallback(
    (
      activeFacet: string | string[] | null | undefined,
      key: string,
      slug: string
    ): IActiveFacetOption[] | undefined => {
      if (!activeFacet) return undefined;

      // term has custom code
      if (typeof activeFacet === "string") return undefined;

      return (
        activeFacet
          .map(
            (option: string): IActiveFacetOption => ({
              label: facetOptionLabels[key][option],
              onClick: () =>
                // remove current option from query string
                setQueryFacets({
                  [slug]: activeFacet?.filter(
                    (item: string) => item !== option
                  ),
                }),
            })
          )
          // if we can't find a label for given key, remove that option
          .filter((activeOption) => !!activeOption.label)
      );
    },
    [facetOptionLabels, setQueryFacets]
  );

  const activeTerm: IActiveFacet | undefined = useMemo(
    () =>
      queryTerm
        ? {
            label: i18nService.copy["toolSearch.keyword"],
            activeOptions: [
              {
                label: queryTerm,
                onClick: resetTerm,
              },
            ],
          }
        : undefined,
    [queryTerm, resetTerm]
  );

  // array of all active facets with their active options
  const activeFacets: IActiveFacet[] = useMemo(
    () => [
      ...(activeTerm ? [activeTerm] : []), // hardcode term
      ...facets
        .map(({ key, label, slug }) => ({
          label,
          activeOptions: getActiveOptions(activeFacetsMap?.[key], key, slug),
        }))
        // if active options has length 0, remove the active facet
        .filter((activeFacet) => !!activeFacet.activeOptions?.length),
    ],
    [facets, activeFacetsMap, getActiveOptions, activeTerm]
  );

  return { activeFacetsMap, activeFacets };
};
